// Singleton service to cache events for unmounted options
class EventCache {
  constructor() {
    this.cache = new Map(); // key: `${id}-${name}`, value: array of {eventType, detail, timestamp}
    this.mountedOptions = new Set(); // track which options are currently mounted
  }

  // Register an option as mounted
  register(id, name) {
    const key = this._makeKey(id, name);
    this.mountedOptions.add(key);
  }

  // Unregister an option when it unmounts
  unregister(id, name) {
    const key = this._makeKey(id, name);
    this.mountedOptions.delete(key);
  }

  // Check if an option is currently mounted
  isMounted(id, name) {
    const key = this._makeKey(id, name);
    return this.mountedOptions.has(key);
  }

  // Add event to cache if option is not mounted
  maybeCache(eventType, detail) {
    // Only cache if the event has id and name (option-specific events)
    if (!detail || !detail.id || !detail.name) {
      return false; // Not an option-specific event
    }

    // If option is mounted, don't cache (it will receive the event normally)
    if (this.isMounted(detail.id, detail.name)) {
      return false;
    }

    // Option is unmounted, cache the event
    const key = this._makeKey(detail.id, detail.name);
    if (!this.cache.has(key)) {
      this.cache.set(key, []);
    }

    this.cache.get(key).push({
      eventType,
      detail: { ...detail }, // Clone to avoid mutation
      timestamp: Date.now()
    });

    return true; // Event was cached
  }

  // Get and clear cached events for an option (called on mount)
  getAndClear(id, name) {
    const key = this._makeKey(id, name);
    const events = this.cache.get(key) || [];
    this.cache.delete(key);
    return events;
  }

  // Helper to create consistent keys
  _makeKey(id, name) {
    return `${id}-${name}`;
  }

  // Debug helpers
  getCacheSize() {
    return this.cache.size;
  }

  getMountedCount() {
    return this.mountedOptions.size;
  }

  debug() {
    console.log('[EventCache] Debug Info:', {
      cachedOptions: Array.from(this.cache.keys()),
      mountedOptions: Array.from(this.mountedOptions),
      totalCachedEvents: Array.from(this.cache.values()).reduce((sum, arr) => sum + arr.length, 0)
    });
  }
}

// Export singleton instance
export default new EventCache();
